
import { defineComponent, ref, reactive, toRefs, onMounted, computed } from 'vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import f10ApiHk from '@/api/f10ApiHk'
import * as echarts from 'echarts'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: { Charts, Nodata, InfoCircleOutlined },
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    theme: {
      type: String,
      default: ''
    }
  },
  setup(props: any) {
    const route = useRoute()
    const state = reactive({
      option: {} as echarts.EChartsOption,
      spinning: true,
      data: [] as any[],
      currentTab: 'dividendAndYield' // 默认选中股息及股息率
    })

    // 处理数据，过滤掉null值
    const processedData = computed(() => {
      return state.data
        .filter(item => item.dividend !== null && item.dividendYield !== null)
        .map(item => {
          const date = new Date(item.fiscalYear)
          return {
            date: `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`,
            year: date.getFullYear(),
            dividend: item.dividend,
            dividendYield: item.dividendYield * 100, // 转换为百分比
            dividendNetIncome: item.dividendNetIncome ? item.dividendNetIncome * 100 : null,
            dividendRate: item.dividendRate ? item.dividendRate * 100 : null
          }
        })
    })

    // 生成图表配置
    const generateChartOption = () => {
      const dates = processedData.value.map(item => item.date)
      const dividends = processedData.value.map(item => item.dividend)

      // 根据当前选中的选项卡确定数据
      let rightData: number[] = []
      let rightName = ''
      let rightSeriesName = ''
      let rightFormatter = '{value}%'
      let leftData: number[] = []
      let leftName = ''
      let leftSeriesName = ''

      switch (state.currentTab) {
        case 'dividendAndYield':
          rightData = processedData.value.map(item => item.dividendYield)
          rightName = '股息率'
          rightSeriesName = '股息率'
          leftData = dividends
          leftName = '股息'
          leftSeriesName = '股息'
          break
        case 'dividendNetIncome':
          leftData = processedData.value.map(item => item.dividendNetIncome || 0)
          leftName = '股息/净利润'
          leftSeriesName = '股息/净利润'
          rightData = []
          rightName = ''
          rightSeriesName = ''
          break
        case 'dividendRate':
          leftData = processedData.value.map(item => item.dividendRate || 0)
          leftName = '股息增长率'
          leftSeriesName = '股息增长率'
          rightData = []
          rightName = ''
          rightSeriesName = ''
          break
      }

      state.option = {
        backgroundColor: props.theme === 'dark' ? '#1f1f1f' : '#fff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985'
            }
          },
          formatter: (params: any) => {
            const date = params[0].name
            const dataItem = processedData.value.find(item => item.date === date)
            if (!dataItem) return ''

            let tooltipHtml = `<div style="margin: 5px 0;font-weight:bold">${date}</div>`

            if (state.currentTab === 'dividendAndYield') {
              tooltipHtml += `<div>股息: ${dataItem.dividend.toFixed(2)}</div>`
              tooltipHtml += `<div>股息率: ${dataItem.dividendYield.toFixed(2)}%</div>`
            } else if (state.currentTab === 'dividendNetIncome') {
              tooltipHtml += `<div>股息/净利润: ${dataItem.dividendNetIncome?.toFixed(2) || '-'}%</div>`
            } else {
              tooltipHtml += `<div>股息增长率: ${dataItem.dividendRate?.toFixed(2) || '-'}%</div>`
            }

            return tooltipHtml
          }
        },
        legend: {
          data: state.currentTab === 'dividendAndYield' ? [leftSeriesName, rightSeriesName] : [leftSeriesName],
          textStyle: {
            color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
          },
          bottom: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            }
          },
          axisLabel: {
            color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d',
            rotate: 0 // 旋转45度防止重叠
          }
        },
        yAxis: state.currentTab === 'dividendAndYield' ? [
          {
            type: 'value',
            name: leftName,
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            axisLabel: {
              formatter: '{value}',
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            splitLine: {
              lineStyle: {
                color: props.theme === 'dark' ? '#2a2a2a' : '#eee'
              }
            }
          },
          {
            type: 'value',
            name: rightName,
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            axisLabel: {
              formatter: rightFormatter,
              color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
            },
            splitLine: {
              show: false
            }
          }
        ] : {
          type: 'value',
          name: leftName,
          nameTextStyle: {
            color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
          },
          axisLabel: {
            formatter: '{value}%',
            color: props.theme === 'dark' ? '#d7d7df' : '#4d4d4d'
          },
          splitLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#2a2a2a' : '#eee'
            }
          }
        },
        series: state.currentTab === 'dividendAndYield' ? [
          {
            name: leftSeriesName,
            type: 'bar',
            barWidth: '10%',
            itemStyle: {
              color: '#1890ff' // 蓝色柱状图
            },
            data: leftData
          },
          {
            name: rightSeriesName,
            type: 'line',
            yAxisIndex: 1,
            symbolSize: 8,
            itemStyle: {
              color: '#faad14' // 黄色折线图
            },
            lineStyle: {
              width: 3
            },
            data: rightData
          }
        ] : [
          {
            name: leftSeriesName,
            type: 'bar',
            barWidth: '10%',
            itemStyle: {
              color: '#1890ff' // 蓝色柱状图
            },
            data: leftData
          }
        ]
      }
    }

    // 切换选项卡
    const changeTab = () => {
      generateChartOption()
    }

    onMounted(() => {
      const data = {
        data: {
          // market: 'HK',
          // symbol: '00700'

          market: route.query.market || 'HK',
          symbol: route.query.code || '00700',
        },
        headers: {
          token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944'
        }
      }

      f10ApiHk.getBriefFinanceDividend(data).then((res: any) => {
        if (res.data?.list) {
          state.data = res.data.list
          generateChartOption()
        }
        state.spinning = false
      }).catch(() => {
        state.spinning = false
      })
    })

    return {
      ...toRefs(state),
      changeTab
    }
  }
})
